import Box from "@mui/material/Box"
import { useCallback, useEffect, useState } from "react"
import { DndProvider, useDrop } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { TouchBackend } from "react-dnd-touch-backend"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import ReactFlow, {
  Background,
  Controls,
  addEdge,
  useEdgesState,
  useNodesState,
} from "reactflow"
import "reactflow/dist/style.css"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import Column from "../../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../../common/components/ConditionalLinearProgress"
import { useIsMobile } from "../../../../common/utils"
import { selectProfileLoaded } from "../../../users/reducer"
import CustomStepEdge from "./CustomStepEdge"
import GlobalState from "./GlobalState"
import {
  TypedCandidatePropertiesContext,
  useLoadCandidateTypedProperties,
} from "./MultiConditionEditor"
import TileSidePanel, { ITEM_TYPE_TILE } from "./TileSidePanel"
import WorkflowToolbar from "./WorkflowToolbar"
import DraggingNode from "./react-flow-nodes/dragging-temp/Node"
import EmailNode from "./react-flow-nodes/email/Node"
import EnterConditionNode from "./react-flow-nodes/entry-condition/Node"
import NoteNode from "./react-flow-nodes/note/Node"
import TaskNode from "./react-flow-nodes/task/Node"
import TimeDelayNode from "./react-flow-nodes/time-delay/Node"
import NODE_TYPES from "./react-flow-nodes/types"
import YesNoSplitNode from "./react-flow-nodes/yes-no-split/Node"
import { EDGE_TYPES, getEdgeFromConnection, getNodeFromDropItem } from "./utils"

export default function WorkflowEditor() {
  const { id: workflowID } = useParams()
  const [workflow, setWorkflow] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const axios = useAuthorizedAxiosClient()
  const profileLoaded = useSelector(selectProfileLoaded)
  const isMobile = useIsMobile()

  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }
      setIsLoading(true)
      axios
        .get(`/workflows/api/workflows/${workflowID}/`)
        .then(({ data }) => setWorkflow(data))
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileLoaded, workflowID]
  )

  return (
    <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
      <Column>
        <ConditionalPurpleLinearProgress visible={isLoading} />
        <Column>
          <WorkflowToolbar
            workflow={workflow}
            setWorkflow={setWorkflow}
            isLoading={isLoading}
          />
          <WorkflowCanvas workflow={workflow} setWorkflow={setWorkflow} />
        </Column>
      </Column>
    </DndProvider>
  )
}

function WorkflowCanvas({ workflow, setWorkflow }) {
  const initialNodes = [
    {
      id: NODE_TYPES.entryCondition,
      type: NODE_TYPES.entryCondition,
      position: { x: 16, y: 10 },
      data: { label: "Start" },
    },
  ]
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const onConnect = useCallback(
    (connection) => {
      setEdges((edges) => addEdge(getEdgeFromConnection(connection), edges))
    },
    [setEdges]
  )
  const [reactFlowInstance, setReactFlowInstance] = useState(null)
  const [dragMousePos, setDragMousePos] = useState(null)
  const [{ isDragging }, dropRef] = useDrop(() => ({
    accept: ITEM_TYPE_TILE,
    collect: (monitor) => ({
      isDragging: monitor.isOver(),
    }),
    drop: (item, monitor) => {
      setDropItem(item)
    },
    hover: (item, monitor) => {
      setDragMousePos(monitor.getClientOffset())
    },
  }))
  const [dropItem, setDropItem] = useState(null)
  const { typedCandidateProperties } = useLoadCandidateTypedProperties()

  function excludeDraggingNode(nodes) {
    return nodes.filter((node) => node.id !== NODE_TYPES.draggingTemp)
  }

  function translateMousePos(xOffset = 0, yOffset = 0) {
    return reactFlowInstance.screenToFlowPosition({
      x: dragMousePos.x + xOffset,
      y: dragMousePos.y + yOffset,
    })
  }

  function handleNodesChange(changes) {
    // prevent deleting entry condition node
    const nextChanges = changes.reduce((acc, change) => {
      if (change.type === "remove" && change.id === NODE_TYPES.entryCondition) {
        return acc
      }
      return [...acc, change]
    }, [])
    onNodesChange(nextChanges)
  }

  useEffect(
    () => {
      const workflowConfig = workflow.config
      if (!workflowConfig || Object.keys(workflowConfig).length === 0) {
        return
      }
      const config = workflow.config

      setNodes(config.nodes)
      setEdges(config.edges)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workflow]
  )

  useEffect(
    () => {
      if (isDragging) {
        setNodes((nodes) =>
          excludeDraggingNode(nodes).concat({
            id: NODE_TYPES.draggingTemp,
            type: NODE_TYPES.draggingTemp,
            position: translateMousePos(-100, -40),
          })
        )
      } else {
        setNodes(excludeDraggingNode)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDragging, dragMousePos]
  )

  useEffect(
    () => {
      if (!dropItem) {
        return
      }
      // must modify nodes in useEffect or all state is null
      setNodes((nodes) =>
        excludeDraggingNode(nodes).concat(
          getNodeFromDropItem(dropItem, translateMousePos)
        )
      )
      setDropItem(null)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dropItem]
  )

  return (
    <TypedCandidatePropertiesContext.Provider value={typedCandidateProperties}>
      <Box sx={{ width: "100%", height: "calc(100vh - 185px)" }}>
        <ReactFlow
          nodes={nodes}
          onNodesChange={handleNodesChange}
          edges={edges}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          proOptions={{ hideAttribution: true }}
          ref={dropRef}
        >
          <Controls />
          <Background variant="dots" gap={12} size={1} />
          <TileSidePanel />
          <GlobalState workflow={workflow} />
        </ReactFlow>
      </Box>
    </TypedCandidatePropertiesContext.Provider>
  )
}

const nodeTypes = {
  [NODE_TYPES.entryCondition]: EnterConditionNode,
  [NODE_TYPES.draggingTemp]: DraggingNode,
  [NODE_TYPES.note]: NoteNode,
  [NODE_TYPES.task]: TaskNode,
  [NODE_TYPES.email]: EmailNode,
  [NODE_TYPES.timeDelay]: TimeDelayNode,
  [NODE_TYPES.yesNoSplit]: YesNoSplitNode,
}

const edgeTypes = {
  [EDGE_TYPES.customstep]: CustomStepEdge,
}
